import React, { useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import HistoryIcon from '@mui/icons-material/History';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DownloadIcon from '@mui/icons-material/Download';
import { getHistoricoAtivo, exportHistoricoAtivo } from '../../../services/carteiraDiariaService';
import { getFundoSucessao } from '../../../services/fundosGerenciadosService';
import { FundoSucessao } from '../../../types/fundosGerenciados';
import { HistoricoAtivoItem } from '../../../types/carteiraDiaria';
import { formatBRL, formatQtd, formatDate } from '../../../utils/formatters';
import { formatCdiSpread } from '../../../utils/expectedReturn';
import { formatRent, thSx, tdSx, SortHeader, SortDir } from './historicoTableUtils';

const PAGE_SIZE = 30;

type SortField = 'data_posicao' | 'quantidade' | 'valor_por_unidade' | 'valor_total';
type CaseType = 'loading' | 'normal' | 'case1' | 'case2';

const INFO_TEXT_DEPARA = [
  'Histórico resolvido via cadastro de ativo (De-Para).',
  '',
  'O escopo inclui todos os registros vinculados ao mesmo',
  'ativo canônico (AtivoMaster), mesmo que ele tenha mudado',
  'de nome ou administradora ao longo do tempo.',
  '',
  'Isso garante continuidade histórica após trocas de ADM.',
].join('\n');

const INFO_TEXT_SEM_DEPARA = [
  'Histórico filtrado pelo nome exato do ativo.',
  '',
  'Este ativo não possui cadastro De-Para, então o histórico',
  'exibe apenas os registros com o nome atual reportado',
  'pela administradora.',
  '',
  'Se o ativo mudou de nome ou administradora no passado,',
  'o período anterior não aparecerá aqui.',
  'Cadastre o ativo em De-Para para resolver isso.',
].join('\n');

// ─── Inner table component ────────────────────────────────────────────────────

interface HistoricoTableCoreProps {
  fundoId: number;
  codigo1: string;
  adm: string;
  nome: string;
  classe?: string;
  subclasse?: string;
  fundoNome?: string; // shown as a section label (Case 2)
  showFundoLabel?: boolean;
}

const HistoricoTableCore: React.FC<HistoricoTableCoreProps> = ({
  fundoId, codigo1, adm, nome, classe, subclasse, fundoNome, showFundoLabel,
}) => {
  const [rows, setRows]           = useState<HistoricoAtivoItem[]>([]);
  const [count, setCount]         = useState(0);
  const [viaDepara, setViaDepara] = useState(false);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sortField, setSortField] = useState<SortField>('data_posicao');
  const [sortDir, setSortDir]     = useState<SortDir>('desc');

  const ordering   = `${sortDir === 'desc' ? '-' : ''}${sortField}`;
  const totalPages = Math.ceil(count / PAGE_SIZE);

  const load = useCallback(() => {
    setLoading(true);
    getHistoricoAtivo(fundoId, codigo1, adm, nome, page, PAGE_SIZE, ordering)
      .then(res => {
        setRows(res.results);
        setCount(res.count);
        setViaDepara(res.via_depara);
      })
      .catch(() => { setRows([]); setCount(0); setViaDepara(false); })
      .finally(() => setLoading(false));
  }, [fundoId, codigo1, adm, nome, page, ordering]);

  useEffect(() => { load(); }, [load]);
  // Reset page when fundoId changes (tab switch)
  useEffect(() => { setPage(1); }, [fundoId]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportHistoricoAtivo(fundoId, codigo1, adm, nome, classe, subclasse);
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field as SortField);
      setSortDir('desc');
    }
    setPage(1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Typography sx={{ py: 3, color: '#8A98A8', textAlign: 'center', fontSize: '13px', fontFamily: 'Inter' }}>
        Sem histórico de posição disponível para este ativo.
      </Typography>
    );
  }

  const showEsperada = rows.some(r => r.rent_esperada_dia != null);
  const showAmort    = rows.some(r => r.amort_total != null && r.amort_total > 0);

  return (
    <Box sx={{ backgroundColor: '#F8FAFC' }}>
      {showFundoLabel && fundoNome && (
        <Box sx={{ px: 2, py: 0.75, backgroundColor: '#EEF2F7', borderBottom: '1px solid #DDE3EB' }}>
          <Typography sx={{ fontSize: '11px', fontFamily: 'Inter', fontWeight: 600, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {fundoNome}
          </Typography>
        </Box>
      )}

      {/* Header: scope indicator + export */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1.25, pb: 0.75 }}>
        <Tooltip title={viaDepara ? 'Histórico via cadastro (De-Para)' : 'Histórico via nome direto'} placement="top">
          {viaDepara
            ? <LinkIcon sx={{ fontSize: 14, color: '#29A152' }} />
            : <LinkOffIcon sx={{ fontSize: 14, color: '#F5A623' }} />
          }
        </Tooltip>
        <Typography sx={{ fontSize: '11px', fontFamily: 'Inter', color: viaDepara ? '#29A152' : '#F5A623', fontWeight: 600 }}>
          {viaDepara ? 'Ativo cadastrado — histórico completo' : 'Sem cadastro — histórico pelo nome atual'}
        </Typography>
        <Tooltip
          title={
            <Box component="pre" sx={{ fontSize: '11px', fontFamily: 'Inter, monospace', whiteSpace: 'pre-wrap', m: 0 }}>
              {viaDepara ? INFO_TEXT_DEPARA : INFO_TEXT_SEM_DEPARA}
            </Box>
          }
          placement="right"
          arrow
        >
          <InfoOutlinedIcon sx={{ fontSize: 14, color: '#B0BEC5', cursor: 'help' }} />
        </Tooltip>

        <Box sx={{ flex: 1 }} />

        <Tooltip title={exporting ? 'Gerando arquivo...' : `Exportar todos os ${count} registros como XLSX`}>
          <span>
            <IconButton
              size="small"
              onClick={handleExport}
              disabled={exporting || count === 0}
              sx={{ p: 0.5, color: exporting ? '#B0BEC5' : '#2C91AD' }}
            >
              {exporting
                ? <CircularProgress size={14} sx={{ color: '#B0BEC5' }} />
                : <DownloadIcon sx={{ fontSize: 16 }} />
              }
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 500 }}>
          <TableHead>
            <TableRow>
              <SortHeader label="Data"             field="data_posicao"      currentField={sortField} currentDir={sortDir} align="left" onSort={handleSort} />
              <SortHeader label="Qtd. Cotas"       field="quantidade"         currentField={sortField} currentDir={sortDir} onSort={handleSort} />
              <SortHeader label="Valor da Cota"    field="valor_por_unidade"  currentField={sortField} currentDir={sortDir} onSort={handleSort} />
              <SortHeader label="Valor Financeiro" field="valor_total"        currentField={sortField} currentDir={sortDir} onSort={handleSort} />
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>Variação (R$)</TableCell>
              {showAmort && (
                <Tooltip title="Amortizações e pagamentos de juros registrados via Movimentações naquela data" placement="top" arrow>
                  <TableCell sx={{ ...thSx, textAlign: 'right', color: '#7B5EA7', cursor: 'default' }}>Amort./Juros</TableCell>
                </Tooltip>
              )}
              {showEsperada && <TableCell sx={{ ...thSx, textAlign: 'right' }}>Var. Esperada (R$)</TableCell>}
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>Rent. Dia</TableCell>
              {showEsperada && <TableCell sx={{ ...thSx, textAlign: 'right' }}>Rent. Esperada</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const rent = formatRent(r.rent_dia);
              const desvio = showEsperada && r.rent_esperada_dia != null && r.rent_esperada_dia !== 0 && r.rent_dia != null
                ? Math.abs(r.rent_dia - r.rent_esperada_dia) > Math.abs(r.rent_esperada_dia) * 0.20
                : false;
              const plAnterior = r.variacao_financeira != null ? r.valor_total - r.variacao_financeira : null;
              const variacaoEsperada = plAnterior != null && r.rent_esperada_dia != null
                ? plAnterior * Math.expm1(r.rent_esperada_dia)
                : null;
              return (
                <TableRow
                  key={r.data_posicao}
                  sx={{
                    backgroundColor: desvio ? '#FFF8E7' : undefined,
                    '&:nth-of-type(even)': { backgroundColor: desvio ? '#FFF3D0' : '#F4F7F9' },
                  }}
                >
                  <TableCell sx={{ ...tdSx, fontWeight: 500 }}>{formatDate(r.data_posicao)}</TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}>{formatQtd(r.quantidade)}</TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}>{formatBRL(r.valor_por_unidade)}</TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right', fontWeight: 600 }}>{formatBRL(r.valor_total)}</TableCell>
                  <TableCell sx={{
                    ...tdSx, textAlign: 'right', fontWeight: 500,
                    color: r.variacao_financeira == null ? '#8A98A8' : r.variacao_financeira > 0 ? '#29A152' : r.variacao_financeira < 0 ? '#E51739' : '#8A98A8',
                  }}>
                    {r.variacao_financeira == null ? '—' : `${r.variacao_financeira >= 0 ? '+' : ''}${formatBRL(r.variacao_financeira)}`}
                  </TableCell>
                  {showAmort && (
                    <TableCell sx={{ ...tdSx, textAlign: 'right', fontWeight: 500, color: r.amort_total ? '#7B5EA7' : '#8A98A8' }}>
                      {r.amort_total ? `+${formatBRL(r.amort_total)}` : '—'}
                    </TableCell>
                  )}
                  {showEsperada && (
                    <TableCell sx={{
                      ...tdSx, textAlign: 'right', fontWeight: 500,
                      color: variacaoEsperada == null ? '#8A98A8' : variacaoEsperada > 0 ? '#6B7A8D' : variacaoEsperada < 0 ? '#E51739' : '#8A98A8',
                    }}>
                      {variacaoEsperada == null ? '—' : `${variacaoEsperada >= 0 ? '+' : ''}${formatBRL(variacaoEsperada)}`}
                    </TableCell>
                  )}
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '12px', fontFamily: 'Inter', fontWeight: 500, color: rent.color, lineHeight: 1.3 }}>
                      {rent.text}
                    </Typography>
                    {r.cdi_dia != null && r.rent_dia != null && (() => {
                      const spread = formatCdiSpread(Math.expm1(r.rent_dia), r.cdi_dia);
                      return spread ? (
                        <Typography sx={{ fontSize: '10px', fontFamily: 'Inter', color: '#8A98A8', fontStyle: 'italic', lineHeight: 1.2 }}>
                          {spread}
                        </Typography>
                      ) : null;
                    })()}
                  </TableCell>
                  {showEsperada && (
                    <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                      {r.rent_esperada_dia == null ? (
                        <Typography sx={{ fontSize: '12px', fontFamily: 'Inter', color: '#8A98A8' }}>—</Typography>
                      ) : (
                        <>
                          <Typography sx={{ fontSize: '12px', fontFamily: 'Inter', fontWeight: 400, color: '#6B7A8D', lineHeight: 1.3 }}>
                            {formatRent(r.rent_esperada_dia).text}
                          </Typography>
                          {r.cdi_dia != null && (() => {
                            const spread = formatCdiSpread(Math.expm1(r.rent_esperada_dia!), r.cdi_dia);
                            return spread ? (
                              <Typography sx={{ fontSize: '10px', fontFamily: 'Inter', color: '#8A98A8', fontStyle: 'italic', lineHeight: 1.2 }}>
                                {spread}
                              </Typography>
                            ) : null;
                          })()}
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Paginação */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, px: 2, py: 0.75, borderTop: '1px solid #EFF2F5' }}>
        <Typography sx={{ fontSize: '11px', color: '#8A98A8', fontFamily: 'Inter' }}>
          {count} registro{count !== 1 ? 's' : ''}
        </Typography>
        <Typography sx={{ fontSize: '11px', color: '#6B7A8D', fontFamily: 'Inter', mx: 0.5 }}>
          Página {page} de {totalPages}
        </Typography>
        <Tooltip title="Página anterior">
          <span>
            <IconButton size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)} sx={{ p: 0.5 }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Próxima página">
          <span>
            <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} sx={{ p: 0.5 }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

// ─── Outer wrapper with succession detection ──────────────────────────────────

export interface HistoricoAtivoSectionProps {
  fundoId: number;
  fundoNome?: string;
  codigo1: string;
  adm: string;
  nome: string;
  classe?: string;
  subclasse?: string;
}

const HistoricoAtivoSection: React.FC<HistoricoAtivoSectionProps> = ({
  fundoId, fundoNome, codigo1, adm, nome, classe, subclasse,
}) => {
  const [caseType, setCaseType]         = useState<CaseType>('loading');
  const [predecessor, setPredecessor]   = useState<FundoSucessao | null>(null);
  const [tabValue, setTabValue]         = useState(0);
  const [showPredecessor, setShowPredecessor] = useState(false);

  useEffect(() => {
    setCaseType('loading');
    setTabValue(0);
    setShowPredecessor(false);

    getFundoSucessao(fundoId).then(async (suc) => {
      if (!suc) {
        setCaseType('normal');
        return;
      }
      setPredecessor(suc);

      // Check if current fund has data for this asset BEFORE the transfer date
      try {
        const oldest = await getHistoricoAtivo(fundoId, codigo1, adm, nome, 1, 1, 'data_posicao');
        if (oldest.count > 0 && oldest.results[0].data_posicao < suc.data_transferencia) {
          // Asset existed in current fund before transfer → Case 1 (tabs)
          setCaseType('case1');
        } else {
          // Asset only came from predecessor → Case 2 (toggle)
          setCaseType('case2');
        }
      } catch {
        setCaseType('case2');
      }
    }).catch(() => {
      setCaseType('normal');
    });
  }, [fundoId, codigo1, adm, nome]);

  if (caseType === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (caseType === 'case1' && predecessor) {
    return (
      <Box>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{ borderBottom: '1px solid #EFF2F5', minHeight: 36, px: 1 }}
          TabIndicatorProps={{ style: { height: 2 } }}
        >
          <Tab
            label={predecessor.fundo_destino_nome || fundoNome || 'Fundo atual'}
            sx={{ fontSize: '12px', fontFamily: 'Inter', minHeight: 36, py: 0.5, textTransform: 'none' }}
          />
          <Tab
            label={predecessor.fundo_origem_nome}
            sx={{ fontSize: '12px', fontFamily: 'Inter', minHeight: 36, py: 0.5, textTransform: 'none' }}
          />
        </Tabs>
        {tabValue === 0 && (
          <HistoricoTableCore
            fundoId={fundoId}
            codigo1={codigo1} adm={adm} nome={nome}
            classe={classe} subclasse={subclasse}
          />
        )}
        {tabValue === 1 && (
          <HistoricoTableCore
            fundoId={predecessor.fundo_origem}
            codigo1={codigo1} adm={adm} nome={nome}
            classe={classe} subclasse={subclasse}
          />
        )}
      </Box>
    );
  }

  if (caseType === 'case2' && predecessor) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.75, borderBottom: '1px solid #EFF2F5' }}>
          <Chip
            icon={<HistoryIcon sx={{ fontSize: '14px !important' }} />}
            label={`Histórico do ${predecessor.fundo_origem_nome}`}
            onClick={() => setShowPredecessor(v => !v)}
            size="small"
            variant={showPredecessor ? 'filled' : 'outlined'}
            color={showPredecessor ? 'primary' : 'default'}
            sx={{
              fontFamily: 'Inter',
              fontSize: '11px',
              height: 24,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          />
        </Box>
        <HistoricoTableCore
          fundoId={fundoId}
          codigo1={codigo1} adm={adm} nome={nome}
          classe={classe} subclasse={subclasse}
          fundoNome={predecessor.fundo_destino_nome || fundoNome}
          showFundoLabel={showPredecessor}
        />
        {showPredecessor && (
          <HistoricoTableCore
            fundoId={predecessor.fundo_origem}
            codigo1={codigo1} adm={adm} nome={nome}
            classe={classe} subclasse={subclasse}
            fundoNome={predecessor.fundo_origem_nome}
            showFundoLabel
          />
        )}
      </Box>
    );
  }

  return (
    <HistoricoTableCore
      fundoId={fundoId}
      codigo1={codigo1} adm={adm} nome={nome}
      classe={classe} subclasse={subclasse}
    />
  );
};

export default HistoricoAtivoSection;